import {
    ImageOpInputsOrNodes,
    ImageOpParameters,
    EvaluatedImageOpParameters,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/data-object/definition"
import {Evaluator} from "@cm/graph-v3"
import {registerImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/register-image-op-node"
import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {
    createImageOpImgProc,
    cropOrZeroExtendImageOpImgProc,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/imgproc/image-op-imgproc-utils"
import {ImageProcessingNodes} from "@cm/image-processing-nodes"
import {JobNodes} from "@cm/job-nodes"
import {ImageOpContextImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-imgproc"
import {ImageOpDataObjectHal} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/data-object/hal"
import {DataObjectImageDescriptor, TexturesApiService} from "@app/textures/service/textures-api.service"
import {ImageOpParametersOfType} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/specializing-image-op-node"
import {getImgProcChannelLayout} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/imgproc-utils"
import {getChannelLayoutByCount} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/hal-utils"
import {ComplexSpecializingImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/complex-specializing-image-op-node"

const nodeDefinition = {...definition, nodeClass: "ImageOpNodeDataObject"}

@registerImageOpNode
export class ImageOpNodeDataObject extends ComplexSpecializingImageOpNode<typeof definition, ImageOpDataObjectHal> {
    override get definition() {
        return nodeDefinition
    }

    constructor(parameters: ImageOpInputsOrNodes<typeof definition>) {
        super(parameters)
    }

    protected async createImageOpHal(
        evaluator: Evaluator<ImageOpContextHal>,
        parameters: EvaluatedImageOpParameters<ImageOpParametersOfType<typeof definition, "hal">>,
    ) {
        const descriptor = await this.resolveDescriptor(parameters, evaluator.context.texturesApi)
        return new ImageOpDataObjectHal(evaluator.context, parameters, descriptor)
    }

    protected async updateImageOpHal(
        evaluator: Evaluator<ImageOpContextHal>,
        imageOpHal: ImageOpDataObjectHal,
        parameters: EvaluatedImageOpParameters<ImageOpParametersOfType<typeof definition, "hal">>,
    ) {
        const descriptor = await this.resolveDescriptor(parameters, evaluator.context.texturesApi)
        imageOpHal.setParametersAndOutputDescriptor(parameters, descriptor)
    }

    protected getHalResult(
        _evaluator: Evaluator<ImageOpContextHal>,
        imageOpHal: ImageOpDataObjectHal,
        _parameters: EvaluatedImageOpParameters<ImageOpParametersOfType<typeof definition, "hal">>,
    ) {
        return imageOpHal
    }

    override toImgProc = async (evaluator: Evaluator<ImageOpContextImgProc>, parameters: ImageOpParametersOfType<typeof definition, "imgproc">) => {
        const evaluatedParameters = await evaluator.getAll(parameters)
        const descriptor = await this.resolveDescriptor(evaluatedParameters, evaluator.context.texturesApi)
        const nativeDescriptor = await this.resolveNativeDescriptor(evaluatedParameters.dataObjectId, evaluator.context.texturesApi)
        const decoded = createImageOpImgProc(
            {...descriptor, width: nativeDescriptor.width, height: nativeDescriptor.height},
            ImageProcessingNodes.convert(
                ImageProcessingNodes.decode(ImageProcessingNodes.externalData(JobNodes.dataObjectReference(evaluatedParameters.dataObjectId), "encodedData")),
                "float32", // make sure it is float32
                getImgProcChannelLayout(getChannelLayoutByCount(descriptor.channels)),
            ),
        )
        return cropOrZeroExtendImageOpImgProc(decoded, nativeDescriptor, descriptor, evaluator.context.createEmptyImage)
    }

    private async resolveNativeDescriptor(dataObjectId: string, texturesApi: TexturesApiService): Promise<DataObjectImageDescriptor> {
        if (dataObjectId !== this.dataObjectDescriptor?.id) {
            this.dataObjectDescriptor = undefined // reset if data object id changed
        }
        this.dataObjectDescriptor ??= await texturesApi.queryDataObjectImageDescriptor(dataObjectId)
        return this.dataObjectDescriptor
    }

    private async resolveDescriptor(parameters: EvaluatedImageOpParameters<ImageOpParameters<typeof definition>>, texturesApi: TexturesApiService) {
        let descriptor = "descriptor" in parameters ? parameters.descriptor : undefined
        if (!descriptor) {
            const nativeDescriptor = await this.resolveNativeDescriptor(parameters.dataObjectId, texturesApi)
            descriptor = {
                width: nativeDescriptor.width,
                height: nativeDescriptor.height,
                channels: "channels" in parameters ? parameters.channels : 3,
            }
        }
        return descriptor
    }

    private dataObjectDescriptor?: DataObjectImageDescriptor
}
